import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Engagement } from "@/types/engagement";
import { EngagementService } from "@/services/engagement-service";

interface EngagementContextType {
  currentEngagement: Engagement | null;
  setCurrentEngagement: (engagement: Engagement | null) => void;
  engagements: Engagement[];
  refreshEngagements: () => void;
}

const EngagementContext = createContext<EngagementContextType | undefined>(undefined);

export function EngagementProvider({ children }: { children: ReactNode }) {
  const [currentEngagement, setCurrentEngagement] = useState<Engagement | null>(null);
  const [engagements, setEngagements] = useState<Engagement[]>([]);

  const refreshEngagements = () => {
    const allEngagements = EngagementService.getAllEngagements();
    setEngagements(allEngagements);
  };

  useEffect(() => {
    refreshEngagements();
    
    // Try to load engagement from URL or localStorage
    const savedEngagementId = localStorage.getItem("current_engagement_id");
    if (savedEngagementId) {
      const engagement = EngagementService.getEngagementById(savedEngagementId);
      if (engagement) {
        setCurrentEngagement(engagement);
      }
    }
  }, []);

  const handleSetCurrentEngagement = (engagement: Engagement | null) => {
    setCurrentEngagement(engagement);
    if (engagement) {
      localStorage.setItem("current_engagement_id", engagement.id);
    } else {
      localStorage.removeItem("current_engagement_id");
    }
  };

  return (
    <EngagementContext.Provider
      value={{
        currentEngagement,
        setCurrentEngagement: handleSetCurrentEngagement,
        engagements,
        refreshEngagements,
      }}
    >
      {children}
    </EngagementContext.Provider>
  );
}

export function useEngagement() {
  const context = useContext(EngagementContext);
  if (context === undefined) {
    throw new Error("useEngagement must be used within an EngagementProvider");
  }
  return context;
}

